// @ts-ignore
import Pagination from "react-js-pagination";
import TinyLoader from "../loaders/TinyLoader";
import { useMemo, useState, useEffect } from "react";

interface PaginationControlsProps {
  activePage: number;
  itemsPerPage: number;
  totalItemCount: number;
  isLoading?: boolean;
  loadingText?: string;
  onPageChange: (pageNumber: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function PaginationControls({
  activePage,
  itemsPerPage,
  totalItemCount,
  isLoading = false,
  loadingText = "Loading...",
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps) {
  // Define default page size options
  const defaultPageSizes = [10, 25, 50, 100];

  // Keep track of all custom page sizes that have been used
  const [customPageSizes, setCustomPageSizes] = useState<number[]>([]);

  // Add current itemsPerPage to customPageSizes if it's not a default
  // Don't add "View All" values (page sizes >= totalItemCount) to customPageSizes
  useEffect(() => {
    if (
      !defaultPageSizes.includes(itemsPerPage) &&
      !customPageSizes.includes(itemsPerPage) &&
      itemsPerPage < totalItemCount // Don't add "View All" values
    ) {
      setCustomPageSizes([...customPageSizes, itemsPerPage]);
    }
  }, [itemsPerPage, customPageSizes, defaultPageSizes, totalItemCount]);

  // Create options array that includes all the custom sizes
  const pageSizeOptions = useMemo(() => {
    const options = [...defaultPageSizes, ...customPageSizes];

    // Remove duplicates and sort
    return Array.from(new Set(options)).sort((a, b) => a - b);
  }, [defaultPageSizes, customPageSizes]);

  // Check if "View All" is selected (itemsPerPage is -1 or >= totalItemCount)
  const isViewAll = itemsPerPage === -1 || itemsPerPage >= totalItemCount;

  return (
    <div className="mt-4 flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-center sm:justify-between bg-white border border-gray-200 rounded-lg shadow-sm p-3 sm:p-4">
      <div className="flex items-center justify-center sm:justify-start">
        <select
          className="w-28 py-1.5 pl-3 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
          value={isViewAll ? -1 : itemsPerPage}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value === -1) {
              // View All - set to totalItemCount or a very large number
              onPageSizeChange(totalItemCount > 0 ? totalItemCount : 999999);
            } else {
              onPageSizeChange(value);
            }
          }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
          <option value={-1}>View All</option>
        </select>
        <span className="ml-2 text-sm text-gray-500">per page</span>
      </div>

      <div className="text-xs sm:text-sm text-gray-700 text-center order-last sm:order-none flex-1">
        <span>
          {isViewAll ? (
            <>Showing all {totalItemCount} items</>
          ) : (
            <>
              Showing {totalItemCount > 0 ? (activePage - 1) * itemsPerPage + 1 : 0}{" "}
              to {Math.min(activePage * itemsPerPage, totalItemCount)} out of{" "}
              {totalItemCount}
            </>
          )}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 min-w-[200px] sm:min-w-[250px] justify-end">
        {isLoading && (
          <div className="flex items-center justify-center w-full sm:w-auto space-x-2">
            <TinyLoader />
            <span className="text-sm text-gray-500">{loadingText}</span>
          </div>
        )}

        {!isViewAll && (
          <div className="w-full sm:w-auto flex justify-center text-sm overflow-x-auto py-2 sm:py-0">
            <Pagination
              activePage={activePage}
              itemsCountPerPage={itemsPerPage}
              totalItemsCount={totalItemCount}
              pageRangeDisplayed={3}
              onChange={onPageChange}
              activeClass="pagination-active-list-item"
              itemClass="pagination-list-item"
              itemClassFirst="pagination-first-item"
              itemClassLast="pagination-last-item"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PaginationControls;
